/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './audio-landing.stache';
import device from 'scripts/utils/device';
import can from 'can';
import settings from 'scripts/utils/settings';
import appContext from 'scripts/app-context';
import _ from 'lodash';
import FeedService from 'scripts/services/base-feed-service';

const moduleSettings = settings.getModuleSettings('live');
const isLiveEnabled = moduleSettings.isEnabled;
const disabledMessage = moduleSettings.unavailabilityMessage;
const inWebView = window.globalSettings.inWebView;

const viewingScheduleUrl = window.globalSettings.pagePaths.viewingSchedule;
const scheduleFeedUrl = window.globalSettings.feeds.videos.liveScheduleUrl;

Component.extend({
    tag: 'audio-landing',
    template: template,
    leakScope: false,
    viewModel: {
        viewingScheduleUrl,
        sponsorIsAvailable: false,
        define: {
            isEnabled: {
                type: 'boolean',
                value: isLiveEnabled,
            },
            disabledMessage: {
                type: 'string',
                value: disabledMessage,
            },
            products: {
                Type: can.Map,
                Value: null,
            },
            productsCtaLink: {
                Type: can.Map,
                Value: null,
            },
            highlightEnded: {
                type: 'number',
            },
            sponsor: {
                Type: can.Map,
                Value: null,
            },
            compatibleVideoPlayer: {
                type: 'boolean',
                value: true,
            },
            liveVideoCards: {
                Type: can.List,
                Value: can.List,
            },
            streamOffMessaging: {
                Type: can.Map,
                Value: can.Map,
            },
            streamOffMessage: {
                Type: can.Map,
                Value: null,
            },
            activeChannels: {
                Type: can.List,
                Value: can.List,
            },
            activeView: {
                type: 'object',
                value: {},
            },
            cards: {
                type: 'array',
                value: [],
            },
            isMobileWeb: {
                type: 'boolean',
                get() {
                    return (
                        ['small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1 && !inWebView
                    );
                },
            },
            isVisibleDesktop: {
                type: 'boolean',
                get() {
                    return (
                        ['medium', 'small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) === -1
                    );
                },
            },
            dataLoaded: {
                type: 'boolean',
                value: false,
            },
            isInPip: {
                type: 'boolean',
                value: false,
            },
            drawerActive: {
                type: 'boolean',
            },
            player: {
                type: 'object',
            },
        },
        handleViewChange(ev, el, { isInPip }) {
            this.attr('isInPip', isInPip);
        },
    },
    init() {
        let incompatibleVideoPlayer = '';

        if (device.isModernIE || device.isOldIE) {
            incompatibleVideoPlayer =
                'INTERNET EXPLORER IS NOT SUPPORTED, FOR OPTIMAL STREAMING PLEASE CHOOSE ANOTHER BROWSER SUCH AS FIREFOX OR CHROME';
        }
        if (incompatibleVideoPlayer) {
            this.viewModel.attr('compatibleVideoPlayer', false);
            this.viewModel.attr('streamOffMessage', {
                videoStreamingOn: false,
                titleText: '',
                streamNotAvailableMessage: incompatibleVideoPlayer,
            });
        } else {
            new FeedService({
                feedUrl: scheduleFeedUrl,
                fromCache: false,
                autoUpdate: true,
            })
                .on('data', (type, data) => {
                    let cards = data.cards || [];

                    cards = cards.filter(
                        (card) => card.channelIdentifier === 'audio',
                    );

                    const statuses = ['live', 'upcoming', 'concluded'];

                    if (cards && cards.length) {
                        let featuredEvent = null;
                        statuses.forEach((status) => {
                            if (!featuredEvent) {
                                cards.forEach((card) => {
                                    if (card.streamState === status) {
                                        featuredEvent = card;
                                        return false;
                                    }
                                });
                            }
                        });

                        this.viewModel.attr('featuredEvent', featuredEvent);
                    }

                    let refresh = this.compareCards(cards);
                    if (refresh) {
                        this.viewModel.attr('activeChannel', null);
                        this.updateCards(cards, true);
                    } else {
                        if (!this.isActiveChannelLive(cards)) {
                            this.viewModel.attr('activeChannel', null);
                        }
                        this.updateCards(cards, false);
                    }
                })
                .on('error', (error) => {
                    console.error('Error fetching live channels', error);
                })
                .fetch();

            new FeedService({
                feedUrl: window.globalSettings.feeds.audio.live,
                fromCache: false,
                requestRate: true,
            })
                .on('data', (type, data) => {
                    this.viewModel.attr(
                        'streamOffMessaging',
                        data.streamOffMessaging,
                    );
                    this.viewModel.attr('sponsor', data.sponsor);
                    this.viewModel.attr(
                        'sponsorIsAvailable',
                        !!_.get(data, 'sponsor.webImage'),
                    );
                    this.viewModel.attr('dataLoaded', true);
                })
                .on('error', (error) => {
                    console.error('Error fetching live channels', error);
                })
                .fetch();
        }
    },
    compareCards: function(newCards) {
        let cards = this.viewModel.attr('cards');
        let update = false;

        if (cards && cards.length) {
            let newliveCardsIds = _.map(newCards, (value, index) => {
                return value.video.identifier;
            });
            let currentCardsIds = _.map(cards, (value, index) => {
                return value.video.identifier;
            });
            if (_.isEqual(newliveCardsIds, currentCardsIds)) {
                update = false;
            } else {
                update = true;
            }
        } else {
            update = true;
        }
        return update;
    },
    updateCards: function(cards, updateStreams) {
        let initChannelId = can.route.attr('channelId'),
            initLoad = this.viewModel.attr('initLoad'),
            liveCards = _.map(cards || [], (card) => {
                if (card) {
                    card.channel = parseInt(
                        _.get(card, 'identifier', '').replace('channel', ''),
                    );

                    if (isNaN(card.channel)) {
                        console.error(
                            `Wrong channel ${card.channel} for audio ${_.get(
                                card,
                                'video.identifier',
                            )}`,
                        );
                        return null;
                    }
                }

                return card;
            }).filter((card) => {
                return card && card.streamState === 'live';
            });

        if (initChannelId) {
            const initChannelIdIndex = _.findIndex(
                liveCards,
                (card) =>
                    card.video &&
                    card.video.identifier &&
                    card.video.identifier == initChannelId,
            );

            if (initChannelIdIndex === -1) {
                initChannelId = null;
                can.route.removeAttr('channelId');
            }
        }

        this.viewModel.attr('cards', cards);
        this.viewModel.attr('liveCards', liveCards);

        const videoPlayerStreams = this.getVideoStreams(initChannelId);

        if (videoPlayerStreams.length && updateStreams) {
            appContext.model.dispatch('live-channel-streams-loaded', [
                videoPlayerStreams,
                true,
                initLoad,
            ]);
        }

        if (initLoad) {
            this.viewModel.attr('initLoad', false);
        }
    },
    getVideoStreams(selectedStreamId) {
        const streams = [];

        _.forEach(this.viewModel.attr('liveCards'), (card) => {
            streams.push({
                id: card.video.identifier,
                channel: card.channel,
                title: card.title,
            });
        });

        _.sortBy(streams, ['channel']);

        if (selectedStreamId) {
            const selectedStreamIndex = _.findIndex(
                streams,
                (stream) => stream.id == selectedStreamId,
            );

            streams.unshift(...streams.splice(selectedStreamIndex, 1));
        }
        return streams;
    },
    isActiveChannelLive: function(cards) {
        let isLive = _.find(cards, (channel) => {
            if (
                channel &&
                channel.video &&
                channel.video.identifier == this.viewModel.attr('activeChannel')
            ) {
                return true;
            }
        });

        return isLive;
    },
});
